import React, { useCallback, useState } from "react";
import ReactDOM from "react-dom";
import { Box, Typography, CircularProgress, Button } from "@mui/material";
import { useRecoilState, useSetRecoilState, useRecoilValue, useRecoilCallback } from 'recoil';
import { atom_activeStopState } from '@app/shared/state/active-stop.atom';
import { atom_listModeState } from '@app/shared/state/list-mode.atom';
import { atom_isDesktop, atom_tourState, atom_routeToStopLocation, atom_isNavigationMode, atom_showNavigationBubble } from '@app/shared/state/tour.atom';
import { atom_geolocationState } from '@app/shared/state/geolocation.atom';
import { atom_showModalContent, atom_modalType } from '@app/shared/state/show-content-modal.atom';
import { atom_openedStopState } from '@app/shared/state/opened-stop.atom';
import { passiveCollector } from '@app/shared/services/passive-data/passive-data-collector';
import { useTranslation } from 'react-i18next';
import WazeMapsIcon from '@app/shared/components/wazeMaps.svg';
import GoogleMapsIcon from '@app/shared/components/googleMaps.jpeg';
import AppleMapsIcon from '@app/shared/components/appleMaps.jpeg';
import { fetchTransitRoute, TravelMode } from '@app/shared/services/transit.service';
import { routeToStopLocation } from '@app/shared/models/tour.type';
import { isIOS } from '@app/shared/utils/env';
import { IOSLocationHelpInline } from '@app/shared/components/IOSLocationHelpInline';
import { Z_INDEX } from '@app/shared/styles/zIndex';

interface MobileNavigationContentProps {
  contentRef?: React.RefObject<HTMLDivElement>;
  preventMapInteraction?: (e: React.MouseEvent | React.TouchEvent) => void;
}

export const MobileNavigationContent: React.FC<MobileNavigationContentProps> = ({
  contentRef,
  preventMapInteraction,
}) => {
  const { t, i18n } = useTranslation();
  const activeStop = useRecoilValue(atom_activeStopState);
  const setListMode = useSetRecoilState(atom_listModeState);
  const isDesktop = useRecoilValue(atom_isDesktop);
  const activeTour = useRecoilValue(atom_tourState);
  const hasSidebar = String(activeTour?.config?.sidebar) !== 'false';
  const [state_routeToStopLocation, setState_RouteToStopLocation] = useRecoilState(atom_routeToStopLocation);
  const setIsNavigationMode = useSetRecoilState(atom_isNavigationMode);
  const setShowNavigationBubble = useSetRecoilState(atom_showNavigationBubble);
  const setState_showModal = useSetRecoilState(atom_showModalContent);
  const setState_modalType = useSetRecoilState(atom_modalType);
  const [state_openedStopState, setState_openedStopState] = useRecoilState(atom_openedStopState);
  const [isLoadingTransit, setIsLoadingTransit] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const isClosing = false; // Exit animation now handled by ListRender cross-fade
  const [selectedMode, setSelectedMode] = useState<TravelMode | null>(null);
  const [showOtherApps, setShowOtherApps] = useState(false);

  const bottomBarHeight = 60; // Height of the mobile bottom bar

  // Detect RTL language (Hebrew)
  const isRTL = i18n.language === 'he';

  // Get stop location
  const latitude = activeStop?.location?.latitude || activeStop?.stop_location?.latitude;
  const longitude = activeStop?.location?.longitude || activeStop?.stop_location?.longitude;

  // Title constant
  const title = t('navigation.view_direction') || 'View Direction';

  // Get default list mode based on device and config
  const getDefaultListMode = useCallback(() => {
    if (isDesktop) return 'desktop';
    if (activeTour?.config?.['show_list']) return 'mobileList';
    return 'mobileSwiper';
  }, [isDesktop, activeTour?.config]);

  // Close navigation and return to default list mode
  const handleClose = useCallback(() => {
    setListMode(getDefaultListMode());
  }, [setListMode, getDefaultListMode]);

  // Handle mode selection
  const handleModeSelect = (mode: TravelMode) => {
    setSelectedMode(mode);
    getTransitData(mode);
  };

  // Transit data fetching function
  const getTransitData = useRecoilCallback(({ snapshot }) => async (mode: TravelMode) => {
    setIsLoadingTransit(true);
    setLocalError(null); // Clear any previous error

    const geolocation = await snapshot.getPromise(atom_geolocationState);

    if (!geolocation.latitude || !geolocation.longitude) {
      let errorMessage = t('directions.location_failed');
      if (geolocation.error) {
        if (geolocation.error.code === 1) {
          errorMessage = t('directions.location_permission_denied');
        } else if (geolocation.error.code === 2) {
          errorMessage = t('directions.location_unavailable');
        } else if (geolocation.error.code === 3) {
          errorMessage = t('directions.location_timeout');
        }
      }
      console.error(errorMessage);
      setLocalError(errorMessage);
      setIsLoadingTransit(false);
      return; // Don't close - show error in this component
    }

    try {
      const { latitude: userLat, longitude: userLng } = geolocation;
      const language = activeTour?.language || 'en';
      console.log('[ROUTE-DEBUG] MobileNavigationContent fetching route', {
        activeStopId: activeStop?.id,
        activeStopTitle: activeStop?.s_title,
        openedStopId: state_openedStopState?.id,
        openedStopTitle: state_openedStopState?.s_title,
        sameStop: activeStop?.id === state_openedStopState?.id,
        destLat: activeStop?.stop_location?.latitude,
        destLng: activeStop?.stop_location?.longitude,
        userLat,
        userLng,
        mode,
      });
      const transitData = await fetchTransitRoute(
        userLat,
        userLng,
        activeStop?.stop_location?.latitude ?? 0,
        activeStop?.stop_location?.longitude ?? 0,
        language,
        mode
      );
      console.log('[ROUTE-DEBUG] fetchTransitRoute returned', {
        hasData: !!transitData,
        routeCount: transitData?.routes?.length ?? (transitData ? 1 : 0),
        firstRouteHasLegs: !!(transitData?.routes?.[0]?.legs?.length),
        firstRouteLegSteps: transitData?.routes?.[0]?.legs?.[0]?.steps?.length,
      });
      if (transitData) {
        const stopImage = activeStop?.loc_pics?.[0]?.url;
        const stopTitle = activeStop?.s_title;

        // Handle both formats: routes array (new) or single route (old)
        const routes = transitData.routes || [transitData];
        if (routes.length > 0) {
          const firstRoute = routes[0];
          setState_RouteToStopLocation({
            ...firstRoute,
            stopImage,
            stopTitle,
            allRoutes: routes.length > 1 ? routes as any : undefined,
            selectedRouteIndex: routes.length > 1 ? 0 : undefined,
            travelMode: mode,
            tourId: activeTour?.id,
          } as routeToStopLocation);
          // Close any open modals and big stop page
          setState_showModal(false);
          setState_modalType(null);
          setState_openedStopState(undefined);
          setIsNavigationMode(true); // Ensure navigation mode is on so RouteListContent appears
          setShowNavigationBubble(true); // Show the navigate pill in MapFilters from the moment navigation starts
          setIsLoadingTransit(false);
          passiveCollector.trackNavigationUsed('in_app_' + mode);
          handleClose(); // Only close on success
        } else {
          setLocalError(t('directions.transit_fetch_failed') || 'No route was found');
          setIsLoadingTransit(false);
        }
      } else {
        setLocalError(t('directions.transit_fetch_failed') || 'No route was found');
        setIsLoadingTransit(false);
        // Don't close - show error
      }
    } catch (error: any) {
      console.error('Error fetching transit data:', error);
      const failedMode = (error as any)?.mode as string | undefined;
      const isNoRoute = typeof error?.message === 'string' && error.message.startsWith('NO_ROUTE_FOR_MODE:');
      let errorMessage: string;
      if (isNoRoute && failedMode) {
        const modeLabels: Record<string, string> = {
          transit: t('directions.mode_transit') || 'transit',
          walking: t('directions.mode_walking') || 'walking',
          bicycling: t('directions.mode_bicycling') || 'bicycling',
          driving: t('directions.mode_driving') || 'driving',
        };
        const modeLabel = modeLabels[failedMode] || failedMode;
        errorMessage = (t('directions.no_route_for_mode', { mode: modeLabel }) as string)
          || `No ${modeLabel} route is available between these points.`;
      } else {
        errorMessage = error instanceof Error ? error.message : (t('directions.transit_fetch_failed') || 'Failed to fetch route');
      }
      setLocalError(errorMessage);
      setIsLoadingTransit(false);
      // Don't close - show error
    }
  }, [t, activeStop, setState_RouteToStopLocation, setState_showModal, setState_modalType, setState_openedStopState, handleClose]);

  // Prevent map interaction handler (fallback if not provided)
  const handlePreventMapInteraction = preventMapInteraction || ((e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
  });

  // Navigation handlers
  const destination = latitude && longitude ? `${latitude},${longitude}` : '';
  const encodedDestination = encodeURIComponent(destination);

  const openInNewTab = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleGoogleMaps = () => {
    passiveCollector.trackNavigationUsed('google_maps');
    openInNewTab(`https://www.google.com/maps/dir/?api=1&destination=${encodedDestination}`);
    handleClose();
  };

  const handleWaze = () => {
    passiveCollector.trackNavigationUsed('waze');
    openInNewTab(`https://www.waze.com/ul?ll=${encodedDestination}&navigate=yes`);
    handleClose();
  };

  const handleAppleMaps = () => {
    passiveCollector.trackNavigationUsed('apple_maps');
    openInNewTab(`http://maps.apple.com/?daddr=${encodedDestination}`);
    handleClose();
  };

  // Mobile navigation row component
  const MobileNavRow = ({
    icon,
    label,
    onClick,
    isExternal = false
  }: {
    icon: React.ReactNode;
    label: string;
    onClick: () => void;
    isExternal?: boolean;
  }) => (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        py: 1.5,
        cursor: 'pointer',
        bgcolor: '#F6F6F9',
        borderRadius: '12px',
        mb: 1,
        '&:active': {
          bgcolor: '#E8E8EB',
        },
      }}
      role="menuitem"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {icon}
        <Typography sx={{
          fontSize: '15px',
          fontWeight: 500,
          color: '#333',
        }}>
          {label}
        </Typography>
      </Box>
      {isExternal && (
        <i
          className="fa-regular fa-arrow-up-right-from-square"
          style={{ fontSize: '14px', color: '#999' }}
        />
      )}
    </Box>
  );

  // Render the component using Portal to escape stacking context
  const content = (
    <Box
      dir={isRTL ? 'rtl' : 'ltr'}
      sx={{
        boxShadow: '0px -4px 10px 0px rgba(0,0,0,0.15)',
        backgroundColor: 'white',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        textAlign: isRTL ? 'right' : 'left',
        position: 'fixed',
        zIndex: Z_INDEX.navigationPanel,
        borderRadius: '20px 20px 0px 0px',
        bottom: hasSidebar ? bottomBarHeight : 0,
        left: 0,
        right: 0,
        pointerEvents: 'auto',
        // Smooth closing animation
        transform: isClosing ? 'translateY(100%)' : 'translateY(0)',
        transition: 'transform 0.3s ease-out',
      }}
      onClick={handlePreventMapInteraction}
      onTouchStart={handlePreventMapInteraction}
      onTouchMove={handlePreventMapInteraction}
      onMouseDown={handlePreventMapInteraction}
      onMouseMove={handlePreventMapInteraction}
    >
      {/* Header with close button */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        px: 2,
        pt: 2,
        pb: 1,
      }}>
        <Button
          onClick={handleClose}
          aria-label={t('close') || 'Close'}
          variant="text"
          disableRipple
          sx={{
            minWidth: 'auto',
            p: 0.5,
            color: '#666',
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'transparent',
              color: '#333',
            }
          }}
        >
          <i className="fa-regular fa-xmark" style={{ fontSize: '20px' }} />
        </Button>
      </Box>

      {/* Navigation options */}
      <Box
        ref={contentRef}
        sx={{
          overflowY: 'auto',
          px: 2,
          pb: 3,
          flex: 1,
        }}
      >
        {/* Travel mode selection — shown directly */}
        <Box sx={{ p: 1.5, mb: 1 }}>
              <Box sx={{ display: 'flex', width: '100%', gap: 1 }}>
                {/* Walking */}
                <Box
                  onClick={() => !isLoadingTransit && handleModeSelect('walking')}
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.75,
                    py: 1,
                    px: 1.5,
                    cursor: isLoadingTransit ? 'default' : 'pointer',
                    borderRadius: '20px',
                    bgcolor: selectedMode === 'walking' ? '#E3F2FD' : '#FFFFFF',
                    border: selectedMode === 'walking' ? '1.5px solid #1976D2' : '1px solid #E5E7EB',
                    transition: 'all 0.2s ease',
                    '&:hover': { bgcolor: selectedMode === 'walking' ? '#E3F2FD' : '#F9FAFB' },
                    '&:active': { bgcolor: '#E3F2FD' },
                    opacity: isLoadingTransit && selectedMode !== 'walking' ? 0.5 : 1,
                  }}
                >
                  {isLoadingTransit && selectedMode === 'walking' ? (
                    <CircularProgress size={16} sx={{ color: '#48526B' }} />
                  ) : (
                    <i className="fa-solid fa-person-walking" style={{ fontSize: '14px', color: '#48526B' }} />
                  )}
                  <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#48526B' }}>
                    {t('navigation.mode_walking')}
                  </Typography>
                </Box>

                {/* Bicycling */}
                <Box
                  onClick={() => !isLoadingTransit && handleModeSelect('bicycling')}
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.75,
                    py: 1,
                    px: 1.5,
                    cursor: isLoadingTransit ? 'default' : 'pointer',
                    borderRadius: '20px',
                    bgcolor: selectedMode === 'bicycling' ? '#FFF3E0' : '#FFFFFF',
                    border: selectedMode === 'bicycling' ? '1.5px solid #F57C00' : '1px solid #E5E7EB',
                    transition: 'all 0.2s ease',
                    '&:hover': { bgcolor: selectedMode === 'bicycling' ? '#FFF3E0' : '#F9FAFB' },
                    '&:active': { bgcolor: '#FFF3E0' },
                    opacity: isLoadingTransit && selectedMode !== 'bicycling' ? 0.5 : 1,
                  }}
                >
                  {isLoadingTransit && selectedMode === 'bicycling' ? (
                    <CircularProgress size={16} sx={{ color: '#48526B' }} />
                  ) : (
                    <i className="fa-solid fa-bicycle" style={{ fontSize: '14px', color: '#48526B' }} />
                  )}
                  <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#48526B' }}>
                    {t('navigation.mode_bicycling')}
                  </Typography>
                </Box>

                {/* Driving */}
                <Box
                  onClick={() => !isLoadingTransit && handleModeSelect('driving')}
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.75,
                    py: 1,
                    px: 1.5,
                    cursor: isLoadingTransit ? 'default' : 'pointer',
                    borderRadius: '20px',
                    bgcolor: selectedMode === 'driving' ? '#FCE4EC' : '#FFFFFF',
                    border: selectedMode === 'driving' ? '1.5px solid #C2185B' : '1px solid #E5E7EB',
                    transition: 'all 0.2s ease',
                    '&:hover': { bgcolor: selectedMode === 'driving' ? '#FCE4EC' : '#F9FAFB' },
                    '&:active': { bgcolor: '#FCE4EC' },
                    opacity: isLoadingTransit && selectedMode !== 'driving' ? 0.5 : 1,
                  }}
                >
                  {isLoadingTransit && selectedMode === 'driving' ? (
                    <CircularProgress size={16} sx={{ color: '#48526B' }} />
                  ) : (
                    <i className="fa-solid fa-car" style={{ fontSize: '14px', color: '#48526B' }} />
                  )}
                  <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#48526B' }}>
                    {t('navigation.mode_driving')}
                  </Typography>
                </Box>

                {/* Transit */}
                <Box
                  onClick={() => !isLoadingTransit && handleModeSelect('transit')}
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.75,
                    py: 1,
                    px: 1.5,
                    cursor: isLoadingTransit ? 'default' : 'pointer',
                    borderRadius: '20px',
                    bgcolor: selectedMode === 'transit' ? '#E8F5E9' : '#FFFFFF',
                    border: selectedMode === 'transit' ? '1.5px solid #388E3C' : '1px solid #E5E7EB',
                    transition: 'all 0.2s ease',
                    '&:hover': { bgcolor: selectedMode === 'transit' ? '#E8F5E9' : '#F9FAFB' },
                    '&:active': { bgcolor: '#E8F5E9' },
                    opacity: isLoadingTransit && selectedMode !== 'transit' ? 0.5 : 1,
                  }}
                >
                  {isLoadingTransit && selectedMode === 'transit' ? (
                    <CircularProgress size={16} sx={{ color: '#48526B' }} />
                  ) : (
                    <i className="fa-solid fa-bus" style={{ fontSize: '14px', color: '#48526B' }} />
                  )}
                  <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#48526B' }}>
                    {t('navigation.mode_transit')}
                  </Typography>
                </Box>
              </Box>
            </Box>

        {/* "or" divider line — separates in-app navigation from external apps */}
        <Box sx={{ display: 'flex', alignItems: 'center', my: 1, px: 1 }}>
          <Box sx={{ flex: 1, height: '1px', bgcolor: '#E0E0E0' }} />
          <Typography sx={{ mx: 1.5, color: '#999', fontSize: '14px', fontWeight: 500 }}>
            {t('navigation.or')}
          </Typography>
          <Box sx={{ flex: 1, height: '1px', bgcolor: '#E0E0E0' }} />
        </Box>

        {/* Other apps — collapsed by default, expands to Waze / Google / Apple icons */}
        {!showOtherApps ? (
          <Box
            onClick={() => setShowOtherApps(true)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: 1.5,
              px: 2,
              py: 1.5,
              cursor: 'pointer',
              bgcolor: '#F6F6F9',
              borderRadius: '12px',
              mb: 1,
              '&:active': { bgcolor: '#E8E8EB' },
            }}
            role="menuitem"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowOtherApps(true); }
            }}
          >
            <i className="fa-regular fa-globe" style={{ fontSize: '20px', color: '#48526B' }} />
            <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#48526B' }}>
              {t('navigation.other_apps')}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 1.5 }}>
            {/* Waze */}
            <Box
              onClick={handleWaze}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                p: 0.5,
                '&:active': { transform: 'scale(0.95)' },
              }}
            >
              <Box sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                bgcolor: '#33CCFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
              }}>
                <img src={WazeMapsIcon} alt="Waze" style={{ width: 24, height: 24 }} />
              </Box>
            </Box>

            {/* Divider */}
            <Box sx={{ width: '1px', height: 28, bgcolor: '#E0E0E0', mx: 2 }} />

            {/* Google Maps */}
            <Box
              onClick={handleGoogleMaps}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                p: 0.5,
                '&:active': { transform: 'scale(0.95)' },
              }}
            >
              <Box sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
              }}>
                <img src={GoogleMapsIcon} alt="Google Maps" style={{ width: 44, height: 44 }} />
              </Box>
            </Box>

            {/* Apple Maps (iOS only) */}
            {isIOS && (
              <>
                {/* Divider */}
                <Box sx={{ width: '1px', height: 28, bgcolor: '#E0E0E0', mx: 2 }} />
                <Box
                  onClick={handleAppleMaps}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    p: 0.5,
                    '&:active': { transform: 'scale(0.95)' },
                  }}
                >
                  <Box sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                  }}>
                    <img src={AppleMapsIcon} alt="Apple Maps" style={{ width: 44, height: 44 }} />
                  </Box>
                </Box>
              </>
            )}
          </Box>
        )}

        {/* Error display */}
        {localError && (
          <Box sx={{ p: 2, textAlign: 'center', mt: 1, bgcolor: '#FFF3F3', borderRadius: '8px' }}>
            <Typography variant="body2" sx={{ fontSize: 14, color: '#d32f2f', wordBreak: 'normal', overflowWrap: 'break-word' }}>
              {localError}
            </Typography>
            <IOSLocationHelpInline />
          </Box>
        )}
        </Box>
      </Box>
  );

  // Use Portal to render inside .App so it picks up accessibility zoom
  const portalTarget = document.querySelector('.App') || document.body;
  return ReactDOM.createPortal(content, portalTarget);
};

export default MobileNavigationContent;
